import React, { useEffect, useState } from 'react';
import { useMediaType } from '../../Hooks/useMediaTypeHook';
import { PaginationResponse } from '../../Interfaces/Api/Pagination';
import css from './PaginationButtons.module.css';
import BasicButton from '../../Ui/Buttons/BasicButton/BasicButton';

interface PaginatedListProps {
    pagination: PaginationResponse;
    changePage: (page: number) => void;
    controlsClassName?: string;
    paginationBtnClassName?: string;
    pageBtnClassName?: string;
    activePageBtnClassName?: string;
}

export const PaginationButtons: React.FC<PaginatedListProps> = ({
    pagination,
    changePage,
    controlsClassName,
    paginationBtnClassName,
    pageBtnClassName,
    activePageBtnClassName,
}) => {
    const [isDesktop, isTablet, isMobile] = useMediaType();
    const [currentPage, setCurrentPage] = useState(pagination.currentPage);
    const totalPages = pagination.lastPage;
    const activeButtonClassName = activePageBtnClassName
        ? activePageBtnClassName
        : css.activePageBtn;

    useEffect(() => {
        setCurrentPage(pagination.currentPage);
    }, [pagination.currentPage]);

    const goToPreviousPage = () => {
        handleChangePage(currentPage - 1);
    };

    const goToNextPage = () => {
        handleChangePage(currentPage + 1);
    };

    const handleChangePage = (page: number) => {
        setCurrentPage(page);
        changePage(page);
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = isMobile ? 1 : 3;
        let startPage = Math.max(
            1,
            currentPage - Math.floor(maxVisiblePages / 2)
        );
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        if (startPage > 1) {
            pageNumbers.unshift('...');
            pageNumbers.unshift(1);
        }
        if (endPage < totalPages) {
            pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div>
            {totalPages > 1 && (
                <div
                    className={`${controlsClassName ? controlsClassName : css.paginationControls}`}
                >
                    <BasicButton
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${paginationBtnClassName ? paginationBtnClassName : css.paginationButton}`}
                    >
                        Prev
                    </BasicButton>

                    {pageNumbers.map((page, index) => (
                        <BasicButton
                            key={index}
                            onClick={() =>
                                typeof page === 'number' &&
                                handleChangePage(page)
                            }
                            className={`${pageBtnClassName ? pageBtnClassName : css.pageBtn} ${currentPage === page ? activeButtonClassName : ''}`}
                            disabled={page === '...'}
                        >
                            {page}
                        </BasicButton>
                    ))}

                    <BasicButton
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${paginationBtnClassName ? paginationBtnClassName : css.paginationButton}`}
                    >
                        Next
                    </BasicButton>
                </div>
            )}
        </div>
    );
};
